import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CommonIcons } from 'common-src/assets/Icons';
import { Button, Menu } from 'common-src/components/base';
import { getCurrentUserId, getRoleConfig } from 'common-src/features/auth';
import { apiRequest, HttpMethods } from 'common-src/features/rest';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import Patient, {
  BgmAlertStatus,
  PlatformStatus,
  PlatformStatusType,
  removeAlerts,
  reopenAccount,
  sendAIGuidedScreeningLink,
  sendScreeningLink,
  updatePatient,
} from 'common-src/models/Patient';
import {
  getBookedAppointmentsFromAthena,
  getChangedAppointments,
  getPatientIntegrationsAthena,
  sendMocaResultsToAthena,
  updateAthenaPatientDemographics,
} from 'common-src/models/PatientIntegrationsAthena';
import {
  createPatientInteractionApiRequest,
  getLastPatientDocumentByModuleType,
  InteractionReason,
  InteractionType,
  moduleTypes,
} from 'common-src/models/PatientInteraction';
import { getSettingByName, SettingName } from 'common-src/models/Setting';
import { ColorsNew } from 'common-src/styles';
import { uuidv4 } from 'common-src/utils/stringUtils';

import Icons from 'src/assets/Icons';
import { ModalType } from 'src/components/base/ModalGroup';
import { openModal } from 'src/features/modals';
import { getPatientNote } from 'src/features/tabsState';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import useNoteCreation from 'src/hooks/useNoteCreation';
import useOpenWindow from 'src/hooks/useOpenWindow';

import commonStyles from '../commonStyles.module.scss';
import OptionItem from '../OptionItem';
import styles from './PatientActionsButton.module.scss';

const PatientActionsButton = ({ patient }) => {
  const {
    id: patientId,
    isDiabetesOnHold,
    canCloseDiabetesAccount,
    canCloseCognitiveAccount,
    diabetesStatus,
    bgmAlertStatus,
    cognitiveStatus,
    isCGM,
    isGuide,
  } = patient;

  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [isLinksLimitReached, setIsLinksLimitReached] = useState(false);
  const [currentAIGuidedScreeningsCount, setCurrentAIGuidedScreeningsCount] = useState(0);

  const dispatch = useDispatch();

  const actions = useCustomSelector((state) => getRoleConfig(state)?.patientChart?.actions) || {};
  const patientNoteId = useCustomSelector((state) => getPatientNote(state, patientId));
  const currentUserId = useCustomSelector((state) => getCurrentUserId(state));
  const isVideoCallsEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'videoCalls'));
  const isHedisEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'hedis'));
  const isMindyLinksEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'mindyLinks'));
  const isFullMocaEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'fullMoca'));
  const isGuideEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'guide'));
  const aiGuidedScreeningsCountLimit = useCustomSelector((state) =>
    getSettingByName(state, SettingName.AIGuidedScreeningsCountLimit),
  );
  const isAIAssistedScreeningEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'AIassistedScreening'),
  );

  const isAthenaEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'athena'));
  const patientIntegrationsAthena = useCustomSelector((state) =>
    getPatientIntegrationsAthena(state, patientId),
  );
  const lastMocaDocument = useCustomSelector((state) =>
    getLastPatientDocumentByModuleType(state, patientId, moduleTypes.MoCABlind.id),
  );

  const hasActiveAthenaIntegrationRecord = patientIntegrationsAthena?.some(
    (integration) => !integration.deletedAt,
  );

  const hasAthenaIntegrationRecord =
    patientIntegrationsAthena && patientIntegrationsAthena.length > 0;

  const { createNote } = useNoteCreation(patient);
  const { showAlert, AlertType } = useAlert();
  const { openWindow } = useOpenWindow();

  const getAIGuidedScreeningsCount = () => {
    if (!isAIAssistedScreeningEnabled) return;

    const windowStart = moment().subtract(37, 'days').toISOString();
    const windowEnd = moment().toISOString();

    apiRequest({
      endpoint: 'mindyScreenings/aiGuidedCount',
      queryParams: {
        start_time: windowStart,
        end_time: windowEnd,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setCurrentAIGuidedScreeningsCount(data?.count || 0);
        setIsLinksLimitReached(data?.count >= aiGuidedScreeningsCountLimit?.value);
      });
  };

  useInterval(() => getAIGuidedScreeningsCount());

  useEffect(() => {
    getAIGuidedScreeningsCount();
  }, []);

  const getActionParams = (successMessage, errorMessage, successCallback, errorCallback) => ({
    successBlock: () => {
      showAlert(AlertType.Success, '', successMessage);
      successCallback?.();
    },
    errorBlock: () => {
      showAlert(AlertType.Error, '', errorMessage);
      errorCallback?.();
    },
  });

  const createDraft = (moduleTypes = []) => {
    if (patientNoteId) {
      dispatch(
        openModal(ModalType.WARNING, {
          title: 'Notice',
          message:
            'A note is currently in progress. Please pause or finish the current note before attempting to resume another note on the patient’s chart.',
          onSubmit: () => {},
          submitButtonText: 'Close',
          showCancelButton: false,
        }),
      );
      return;
    }

    const documentationId = uuidv4();
    dispatch(
      createPatientInteractionApiRequest({
        body: { patientId, id: documentationId, moduleTypes },
        successBlock: () => {
          openWindow({
            url: `/dashboard-client/documentation/details/${patientId}/${documentationId}/edit`,
            features: 'width=1200,height=800,left=300,top=100',
          });
        },
        errorBlock: (err, errStatus) => {
          showAlert(
            AlertType.Error,
            '',
            `Failed to create documentation.  Err status: ${errStatus}.  Err message: ${err}`,
          );
        },
      }),
    );
  };

  const createNewNote = (typeId, reasonId, moduleTypes = []) => {
    if (patientNoteId) {
      dispatch(
        openModal(ModalType.WARNING, {
          title: 'Notice',
          message:
            'A note is currently in progress. Please pause or finish the current note before attempting to resume another note on the patient’s chart.',
          onSubmit: () => {},
          submitButtonText: 'Close',
          showCancelButton: false,
        }),
      );
      return;
    }

    createNote(typeId, reasonId, moduleTypes);
  };

  const startVideoCall = () => {
    apiRequest({
      endpoint: 'videoCalls',
      method: HttpMethods.Post,
      body: { patientId, clientId: currentUserId },
    })
      .then(async (res) => {
        if (res.status !== 200) {
          const errorBody = await res.json();
          throw new Error(
            errorBody?.user_message ?? errorBody?.detail ?? 'Error while starting video call.',
          );
        }
        return res.json();
      })
      .then((data) => {
        const patientUrl = String(data[`patient-${patientId}`]) ?? '';
        const clientUrl = String(data[`client-${currentUserId}`]) ?? '';

        dispatch(
          openModal(ModalType.VIDEO_CALL_INFORMATION, {
            patientId,
            meetingId: data?.meetingId ?? '',
            patientUrl,
            clientUrl,
          }),
        );
      })
      .catch((error) => showAlert(AlertType.Error, '', error.message));
  };

  const sendMocaResults = () => {
    if (!patientIntegrationsAthena || patientIntegrationsAthena.length === 0) {
      showAlert(
        AlertType.Error,
        '',
        'Patient is not linked to an Athena account. Please link the patient first.',
      );
      return;
    }

    // Get the active integration (non-deleted)
    const activeIntegration = patientIntegrationsAthena.find(
      (integration) => !integration.deletedAt,
    );
    if (!activeIntegration) {
      showAlert(
        AlertType.Error,
        '',
        'No active Athena integration found. Please link the patient first.',
      );
      return;
    }

    // Validate MOCA document exists
    if (!lastMocaDocument) {
      showAlert(
        AlertType.Error,
        '',
        'No MoCA-Blind document found for this patient. Please complete a MoCA assessment first.',
      );
      return;
    }

    dispatch(
      sendMocaResultsToAthena(patientId, {
        successBlock: () => {
          showAlert(AlertType.Success, '', 'MOCA document uploaded successfully to Athena');
        },
        errorBlock: (err) => {
          showAlert(AlertType.Error, '', err);
        },
      }),
    );
  };

  const pullAppointments = () => {
    if (!patientIntegrationsAthena || patientIntegrationsAthena.length === 0) {
      showAlert(
        AlertType.Error,
        '',
        'Patient is not linked to an Athena account. Please link the patient first.',
      );
      return;
    }

    const activeIntegration = patientIntegrationsAthena.find(
      (integration) => !integration.deletedAt,
    );
    if (!activeIntegration) {
      showAlert(
        AlertType.Error,
        '',
        'No active Athena integration found. Please link the patient first.',
      );
      return;
    }

    const today = moment().format('MM/DD/YYYY');
    const endDate = moment().add(90, 'days').format('MM/DD/YYYY');

    // First, poll for changed appointments to update already fetched ones
    dispatch(
      getChangedAppointments(
        {
          patientid: [activeIntegration.athenaPatientId],
          limit: 1000,
        },
        {
          successBlock: (changedData) => {
            const changedCount = Array.isArray(changedData?.appointments)
              ? changedData.appointments.length
              : 0;

            // After processing changed appointments, pull new booked appointments
            dispatch(
              getBookedAppointmentsFromAthena(
                {
                  start_date: today,
                  end_date: endDate,
                  limit: 1000,
                },
                {
                  successBlock: (data) => {
                    // Filter appointments for this specific patient
                    const patientAppointments = Array.isArray(data)
                      ? data.filter(
                          (apt) => apt.athenaPatientId === activeIntegration.athenaPatientId,
                        )
                      : [];

                    const appointmentCount = patientAppointments.length;

                    if (appointmentCount === 0 && changedCount === 0) {
                      showAlert(
                        AlertType.Warning,
                        '',
                        'No upcoming appointments found for this patient in Athena',
                      );
                    } else {
                      const appointmentsList = patientAppointments
                        .map((apt, index) => {
                          const date = apt.sourcePayload?.date || 'N/A';
                          const time = apt.startTime || 'N/A';
                          const type = apt.appointmentType || 'Appointment';
                          const provider = `Provider ID: ${apt.providerAthenaId || 'N/A'}`;
                          return `${index + 1}. ${type}\n   Date: ${date} at ${time}\n   ${provider}\n   ID: ${apt.athenaAppointmentId}`;
                        })
                        .join('\n\n');

                      const changedMessage =
                        changedCount > 0
                          ? `Updated ${changedCount} changed appointment(s).\n\n`
                          : '';

                      showAlert(
                        AlertType.Success,
                        '',
                        `${changedMessage}Successfully found ${appointmentCount} upcoming appointment(s):\n\n${appointmentsList}\n\nView appointments in:\n• Scheduled Calls > All Visits tab\n• Encounters > Athena Appointments tab`,
                      );
                    }
                  },
                  errorBlock: (err) => {
                    showAlert(AlertType.Error, '', err);
                  },
                },
              ),
            );
          },
          errorBlock: () => {
            // If changed appointments fails, still try to pull booked appointments
            dispatch(
              getBookedAppointmentsFromAthena(
                {
                  start_date: today,
                  end_date: endDate,
                  limit: 1000,
                },
                {
                  successBlock: (data) => {
                    const patientAppointments = Array.isArray(data)
                      ? data.filter(
                          (apt) => apt.athenaPatientId === activeIntegration.athenaPatientId,
                        )
                      : [];

                    const appointmentCount = patientAppointments.length;

                    if (appointmentCount === 0) {
                      showAlert(
                        AlertType.Warning,
                        '',
                        'No upcoming appointments found for this patient in Athena',
                      );
                    } else {
                      const appointmentsList = patientAppointments
                        .map((apt, index) => {
                          const date = apt.sourcePayload?.date || 'N/A';
                          const time = apt.startTime || 'N/A';
                          const type = apt.appointmentType || 'Appointment';
                          const provider = `Provider ID: ${apt.providerAthenaId || 'N/A'}`;
                          return `${index + 1}. ${type}\n   Date: ${date} at ${time}\n   ${provider}\n   ID: ${apt.athenaAppointmentId}`;
                        })
                        .join('\n\n');

                      showAlert(
                        AlertType.Success,
                        '',
                        `Successfully found ${appointmentCount} upcoming appointment(s):\n\n${appointmentsList}\n\nView appointments in:\n• Scheduled Calls > All Visits tab\n• Encounters > Athena Appointments tab`,
                      );
                    }
                  },
                  errorBlock: (err) => {
                    showAlert(AlertType.Error, '', err);
                  },
                },
              ),
            );
          },
        },
      ),
    );
    apiRequest({
      endpoint: 'jobRequests/upsertAthenaAppointments',
      method: HttpMethods.Post,
      body: {},
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const hasActiveAthenaIntegration = () => {
    if (!patientIntegrationsAthena || patientIntegrationsAthena.length === 0) {
      return false;
    }
    return patientIntegrationsAthena.some((integration) => !integration.deletedAt);
  };

  const syncAthenaDemographics = () => {
    if (!hasActiveAthenaIntegration()) {
      showAlert(
        AlertType.Error,
        '',
        'Patient is not linked to Athena. Please create the patient on Athena first.',
      );
      return;
    }

    dispatch(
      updateAthenaPatientDemographics(patientId, {
        successBlock: () => {
          showAlert(AlertType.Success, '', 'Demographics synced successfully to Athena');
        },
        errorBlock: (err) => {
          showAlert(AlertType.Error, '', err);
        },
      }),
    );
  };

  const options = useMemo(
    () =>
      _.compact([
        actions.canAddTask && {
          label: <OptionItem id="add-task" icon={CommonIcons.plusIcon} label="Add Task" />,
          title: 'Add Task',
          onClick: async () => dispatch(openModal(ModalType.ADD_TASK, { patientId })),
        },
        actions.canMfaApprove && {
          label: <OptionItem id="approve-mfa" icon={Icons.moneyIcon} label="Approve MFA" />,
          title: 'Approve MFA',
          onClick: async () =>
            createNewNote(InteractionType.Administrative.id, InteractionReason.Administrative.id, [
              moduleTypes.MFADecision.id,
            ]),
        },
        actions.canAddNewIntake && {
          label: <OptionItem id="new-intake" icon={Icons.docIcon} label="New Intake" />,
          title: 'New Intake',
          onClick: async () =>
            createNewNote(InteractionType.Enrollment.id, InteractionReason.Intake.id),
        },
        actions.canSendDocumentation && {
          label: (
            <OptionItem
              id="send-documentation"
              icon={Icons.newDocIcon}
              label="Generate Documentation"
            />
          ),
          title: 'Generate Documentation',
          onClick: async () => dispatch(openModal(ModalType.SEND_DOCUMENTATION, { patientId })),
        },
        actions.canSendBulkDocumentation && {
          label: (
            <OptionItem
              id="send-bulk-documentation"
              icon={Icons.newDocIcon}
              label="Generate Bulk Documentation"
            />
          ),
          title: 'Generate Bulk Documentation',
          onClick: async () => dispatch(openModal(ModalType.SEND_BULK_DOCUMENTATION)),
        },
        isVideoCallsEnabled && {
          label: (
            <OptionItem id="start-video-call" icon={Icons.videoCallIcon} label="Start Video Call" />
          ),
          title: 'Start Video Call',
          onClick: async () => startVideoCall(),
        },
        isHedisEnabled &&
          actions.canSendReferral && {
            label: (
              <OptionItem
                id="generate-referral"
                icon={Icons.newDocIcon}
                label="Generate Referral"
              />
            ),
            title: 'Generate Referral',
            onClick: async () => dispatch(openModal(ModalType.SEND_REFERRAL, { patientId })),
          },
        {
          label: <OptionItem id="diabetes" icon={Icons.medicalIcon} label="Diabetes" />,
          title: 'Diabetes',
          subOptions: _.compact([
            isDiabetesOnHold &&
              actions.canChangeStatusToActive && {
                id: 'status-diabetes-active',
                label: 'Change status to Active',
                onClick: async () => {
                  dispatch(updatePatient(patientId, { diabetesStatus: PlatformStatus.Active }));
                },
              },
            diabetesStatus === PlatformStatus.Closed &&
              actions.canReopenAccount && {
                id: 'reopen-diabetes-account',
                label: 'Re-open Diabetes Account',
                onClick: async () => {
                  dispatch(
                    openModal(ModalType.WARNING, {
                      title: 'Re-open Diabetes Account',
                      message: 'Are you sure?',
                      onSubmit: () =>
                        dispatch(
                          reopenAccount(
                            patientId,
                            PlatformStatusType.Diabetes,
                            getActionParams(
                              'Successfully reopened Diabetes account.',
                              'Failed to reopen Diabetes account.',
                            ),
                          ),
                        ),
                    }),
                  );
                },
              },
            isCGM === true &&
              actions.canSwitchCGMToBGM && {
                id: 'turn-off-cgm',
                label: 'Switch from CGM to BGM',
                onClick: async () => {
                  dispatch(
                    updatePatient(
                      patientId,
                      {
                        isCGM: false,
                        isBGM: true,
                        blockBgmFulfillment: false,
                      },
                      getActionParams(
                        'Successfully switched from CGM to BGM.',
                        'Failed to switch from CGM to BGM.',
                      ),
                    ),
                  );
                },
              },
            actions.canClearBGMAlertStatus &&
              bgmAlertStatus !== BgmAlertStatus.Clear.value && {
                id: 'clear-bgm-alert-status',
                label: 'Clear BGM Alert Status',
                onClick: async () => {
                  dispatch(
                    updatePatient(
                      patientId,
                      {
                        bgmAlertStatus: BgmAlertStatus.Clear.value,
                        bgmAlertStatusAt: moment().utc().toISOString(),
                      },
                      getActionParams(
                        'Successfully cleared BGM alert status.',
                        'Failed to clear BGM alert status.',
                      ),
                    ),
                  );
                },
              },
            actions.canClearBGMAlerts && {
              id: 'clear-bgm-alerts',
              label: 'Clear BGM Alerts',
              onClick: async () => {
                dispatch(
                  removeAlerts(
                    patientId,
                    getActionParams(
                      'Successfully cleared BGM alerts.',
                      'Failed to clear BGM alerts.',
                    ),
                  ),
                );
              },
            },
            actions.canSendLmn && {
              id: 'send-lmn',
              label: 'Generate LMN',
              onClick: async () => dispatch(openModal(ModalType.REQUEST_LMN, { patientId })),
            },
            diabetesStatus === PlatformStatus.Active &&
              actions.canPlaceAccountOnHold && {
                id: 'hold-diabetes-account',
                label: 'Place Diabetes Account on Hold',
                onClick: async () => createDraft([moduleTypes.HoldRequest.id]),
              },
            canCloseDiabetesAccount &&
              actions.canCloseAccount && {
                id: 'close-diabetes-account',
                label: 'Close Diabetes Account',
                onClick: async () => createDraft([moduleTypes.CloseDiabetesAccount.id]),
              },
          ]),
        },
        isMindyLinksEnabled &&
          actions.cognitive && {
            label: <OptionItem id="cognitive" icon={Icons.cognitiveIcon} label="Cognitive" />,
            title: 'Cognitive',
            subOptions: _.compact([
              cognitiveStatus === PlatformStatus.Closed &&
                actions.canReopenAccount && {
                  id: 'reopen-cognitive-account',
                  label: 'Re-open Cognitive Account',
                  onClick: async () => {
                    dispatch(
                      openModal(ModalType.WARNING, {
                        title: 'Re-open Cognitive Account',
                        message: 'Are you sure?',
                        onSubmit: () =>
                          dispatch(
                            reopenAccount(
                              patientId,
                              PlatformStatusType.Cognitive,
                              getActionParams(
                                'Successfully reopened Cognitive account.',
                                'Failed to reopen Cognitive account.',
                              ),
                            ),
                          ),
                      }),
                    );
                  },
                },
              cognitiveStatus === PlatformStatus.Gray &&
                actions.canChangeStatusToActive && {
                  id: 'status-cognitive-active',
                  label: 'Change status to Active',
                  onClick: async () => {
                    dispatch(
                      updatePatient(
                        patientId,
                        { cognitiveStatus: PlatformStatus.Active },
                        getActionParams(
                          'Successfully changed status to Active',
                          'Failed to change status to Active',
                        ),
                      ),
                    );
                  },
                },
              cognitiveStatus !== PlatformStatus.Closed && {
                id: 'send-link-for-moca-screening',
                label: 'Send link for MOCA Screening',
                onClick: () =>
                  dispatch(
                    openModal(ModalType.SEND_SCREENER_LINK, {
                      patientId,
                      onSubmit: (channel, successCallback, errorCallback) =>
                        dispatch(
                          sendScreeningLink(
                            patientId,
                            { moca: true, channel },
                            getActionParams(
                              'Successfully sent link for MOCA Screening',
                              'Failed to send link for MOCA Screening',
                              successCallback,
                              errorCallback,
                            ),
                          ),
                        ),
                    }),
                  ),
              },
              cognitiveStatus !== PlatformStatus.Closed && {
                id: 'send-link-for-moca-and-mis-screening',
                label: 'Send link for MOCA and MIS Screening',
                onClick: () =>
                  dispatch(
                    openModal(ModalType.SEND_SCREENER_LINK, {
                      patientId,
                      onSubmit: (channel, successCallback, errorCallback) =>
                        dispatch(
                          sendScreeningLink(
                            patientId,
                            { moca: true, mis: true, channel },
                            getActionParams(
                              'Successfully sent link for MOCA and MIS Screening',
                              'Failed to send link for MOCA and MIS Screening',
                              successCallback,
                              errorCallback,
                            ),
                          ),
                        ),
                    }),
                  ),
              },
              cognitiveStatus !== PlatformStatus.Closed && {
                id: 'send-link-for-mis-screening',
                label: 'Send link for MIS Screening',
                onClick: () =>
                  dispatch(
                    openModal(ModalType.SEND_SCREENER_LINK, {
                      patientId,
                      onSubmit: (channel, successCallback, errorCallback) =>
                        dispatch(
                          sendScreeningLink(
                            patientId,
                            { mis: true, channel },
                            getActionParams(
                              'Successfully sent link for MIS Screening',
                              'Failed to send link for MIS Screening',
                              successCallback,
                              errorCallback,
                            ),
                          ),
                        ),
                    }),
                  ),
              },
              isFullMocaEnabled &&
                cognitiveStatus !== PlatformStatus.Closed && {
                  id: 'send-link-for-full-moca-screening',
                  label: 'Send link for Full MoCA Screening',
                  onClick: () =>
                    dispatch(
                      openModal(ModalType.SEND_SCREENER_LINK, {
                        patientId,
                        onSubmit: (channel, successCallback, errorCallback) =>
                          dispatch(
                            sendScreeningLink(
                              patientId,
                              { moca_visual: true, channel },
                              getActionParams(
                                'Successfully sent link for Full MoCA Screening',
                                'Failed to send link for Full MoCA Screening',
                                successCallback,
                                errorCallback,
                              ),
                            ),
                          ),
                      }),
                    ),
                },
              isFullMocaEnabled &&
                cognitiveStatus !== PlatformStatus.Closed && {
                  id: 'send-link-for-full-moca-and-mis-screening',
                  label: 'Send link for Full MoCA and MIS Screening',
                  onClick: () =>
                    dispatch(
                      openModal(ModalType.SEND_SCREENER_LINK, {
                        patientId,
                        onSubmit: (channel, successCallback, errorCallback) =>
                          dispatch(
                            sendScreeningLink(
                              patientId,
                              { moca_visual: true, mis: true, channel },
                              getActionParams(
                                'Successfully sent link for Full MoCA and MIS Screening',
                                'Failed to send link for Full MoCA and MIS Screening',
                                successCallback,
                                errorCallback,
                              ),
                            ),
                          ),
                      }),
                    ),
                },
              isAIAssistedScreeningEnabled &&
                cognitiveStatus !== PlatformStatus.Closed && {
                  id: 'send-link-for-ai-avatar-assisted-full-moca-and-mis-screening',
                  label: 'Send Link for AI avatar assisted Full MoCA and MIS Screening',
                  onClick: () =>
                    dispatch(
                      openModal(ModalType.SEND_SCREENER_LINK, {
                        patientId,
                        onSubmit: (channel, successCallback, errorCallback) =>
                          dispatch(
                            sendAIGuidedScreeningLink(
                              patientId,
                              { moca_visual: true, mis: true, channel },
                              getActionParams(
                                'Successfully sent link for AI avatar assisted Full MoCA and MIS Screening',
                                'Failed to send link for AI avatar assisted Full MoCA and MIS Screening',
                                successCallback,
                                errorCallback,
                              ),
                            ),
                          ),
                      }),
                    ),
                  isDisabled: isLinksLimitReached,
                  tooltipText: isLinksLimitReached
                    ? 'Number of Allocated test for AI screening has been reached'
                    : `AI screening tests used: ${currentAIGuidedScreeningsCount} of ${aiGuidedScreeningsCountLimit?.value || 0}`,
                },
              canCloseCognitiveAccount &&
                actions.canCloseAccount && {
                  id: 'close-cognitive-account',
                  label: 'Close Cognitive Account',
                  onClick: async () => createDraft([moduleTypes.CloseCognitiveAccount.id]),
                },
            ]),
          },
        isGuideEnabled && {
          label: <OptionItem id="guide" icon={Icons.teamIcon} label="GUIDE" />,
          title: 'GUIDE',
          subOptions: _.compact([
            !isGuide &&
              actions.canChangeStatusToActive && {
                id: 'activate-guide',
                label: 'Activate GUIDE',
                onClick: async () => {
                  dispatch(
                    updatePatient(
                      patientId,
                      { isGuide: true },
                      getActionParams('Successfully activated GUIDE', 'Failed to activate GUIDE'),
                    ),
                  );
                },
              },
            isGuide && {
              id: 'send-link-for-pro-10-screening',
              label: 'Send link for PRO-10 Screening',
              onClick: () =>
                dispatch(
                  openModal(ModalType.SEND_SCREENER_LINK, {
                    patientId,
                    onSubmit: (channel, successCallback, errorCallback) =>
                      dispatch(
                        sendScreeningLink(
                          patientId,
                          { pro10: true, channel },
                          getActionParams(
                            'Successfully sent link for Pro-10 Screening',
                            'Failed to send link for Pro-10 Screening',
                            successCallback,
                            errorCallback,
                          ),
                        ),
                      ),
                  }),
                ),
            },
            isGuide &&
              actions.canCloseAccount && {
                id: 'deactivate-guide',
                label: 'Deactivate GUIDE',
                onClick: async () => {
                  dispatch(
                    updatePatient(
                      patientId,
                      { isGuide: false },
                      getActionParams(
                        'Successfully deactivated GUIDE',
                        'Failed to deactivate GUIDE',
                      ),
                    ),
                  );
                },
              },
          ]),
        },
        isAthenaEnabled && {
          label: <OptionItem id="athena" icon={Icons.docIcon} label="Athena" />,
          title: 'Athena',
          subOptions: _.compact([
            !hasAthenaIntegrationRecord && {
              id: 'create-athena-patient',
              label: 'Create Patient on Athena',
              onClick: async () =>
                dispatch(openModal(ModalType.CREATE_ATHENA_PATIENT, { patientId })),
            },
            hasActiveAthenaIntegrationRecord && {
              id: 'sync-athena-demographics',
              label: 'Sync Demographics',
              onClick: async () => syncAthenaDemographics(),
            },
            hasActiveAthenaIntegrationRecord && {
              id: 'send-moca-results',
              label: 'Send MOCA Results',
              onClick: async () => sendMocaResults(),
            },
            hasActiveAthenaIntegrationRecord && {
              id: 'pull-appointments',
              label: 'Pull Appointments',
              onClick: async () => pullAppointments(),
            },
          ]),
        },
      ]),
    [
      isDiabetesOnHold,
      canCloseDiabetesAccount,
      canCloseCognitiveAccount,
      diabetesStatus,
      cognitiveStatus,
      patientNoteId,
      bgmAlertStatus,
      isVideoCallsEnabled,
      isHedisEnabled,
      isFullMocaEnabled,
      isGuideEnabled,
      isCGM,
      isGuide,
      isLinksLimitReached,
      currentAIGuidedScreeningsCount,
      patientIntegrationsAthena,
    ],
  );

  return (
    <Menu
      id="patient-actions-button"
      button={
        <Button
          id="actions-button"
          classNames={[styles.button, commonStyles.actionsMenu]}
          iconSrc={CommonIcons.actions}
          text="Actions"
          onClick={() => setIsMenuOpened(true)}
          textColor={ColorsNew.darkGreen}
          backgroundColor={isMenuOpened ? ColorsNew.lighterGreen : 'transparent'}
          borderColor={isMenuOpened ? ColorsNew.lighterGreen : 'transparent'}
          width={105}
        />
      }
      customOptions={options}
      paperWidth={250}
      onCloseMenu={() => setIsMenuOpened(false)}
      subPaperPosition="left"
      subOptionHoverColor={ColorsNew.lighterGreen}
    />
  );
};

PatientActionsButton.propTypes = {
  patient: PropTypes.exact(Patient.schema),
};

export default PatientActionsButton;
